import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, X, Sparkles, Sun, CreditCard, Smartphone, Wallet, Loader2 } from "lucide-react";

const FREE_FEATURES = [
  { text: "Calcolatore ROI illimitato", ok: true },
  { text: "Marketplace installatori", ok: true },
  { text: "Guide, glossario e bandi", ok: true },
  { text: "3 messaggi AI Chat al giorno", ok: true },
  { text: "Dashboard monitoraggio in tempo reale", ok: false },
  { text: "Consumi, autoconsumo, immissione in rete", ok: false },
  { text: "Previsioni produzione, notifiche anomalie", ok: false },
  { text: "Report PDF, controllo dispositivi smart", ok: false },
  { text: "Suggerimenti AI personalizzati, prezzi energia", ok: false },
];

const PRO_FEATURES = [
  { text: "Tutto quello che c'è nel Free", ok: true },
  { text: "Monitoraggio LIVE: produzione, consumi, batteria, rete", ok: true },
  { text: "Storico completo: giornaliero, mensile, annuale", ok: true },
  { text: "Previsioni produzione a 7 giorni (meteo)", ok: true },
  { text: "Notifiche anomalie (sotto-produzione, guasti inverter)", ok: true },
  { text: "Stima risparmio economico in tempo reale", ok: true },
  { text: "Monitoraggio batteria: SoC, flusso, cicli", ok: true },
  { text: "Prezzi energia PUN in tempo reale", ok: true },
  { text: "Report PDF esportabili", ok: true },
  { text: "Controllo dispositivi smart (AC, EV, lavatrice)", ok: true },
  { text: "Suggerimenti AI personalizzati", ok: true },
  { text: "AI Chat SolarControl illimitata", ok: true },
];

export default function Pricing() {
  const { user } = useApp();
  const nav = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null); // "monthly" | "annual" | null

  const startCheckout = async (plan) => {
    if (!user) { nav("/login?next=/pricing"); return; }
    setLoadingPlan(plan);
    try {
      const endpoint = plan === "annual" ? "/payments/annual" : "/payments/checkout";
      const body = plan === "annual"
        ? { origin_url: window.location.origin }
        : { origin_url: window.location.origin, lookup_key: "pro_monthly" };
      const { data } = await api.post(endpoint, body);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore checkout");
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg pb-16">
      <div className="max-w-6xl mx-auto px-6 pt-16">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 label-eco mb-6">
            <Sparkles size={12} className="text-[#EAB308]" /> SolarControl Pro
          </div>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-[#17301C] leading-[0.95]">
            Sblocca tutte le funzioni <span className="italic font-light text-[#EAB308]">Pro</span>.
          </h1>
          <p className="text-lg text-[#3A4A3E] mt-6">
            Un solo abbonamento. <b>Scegli come pagare</b>: mensilmente oppure tutto subito.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* FREE */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-8">
            <div className="label-eco mb-3">Free</div>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="text-5xl font-black font-mono-nums text-[#17301C]">€0</span>
              <span className="text-[#6E7A71]">/mese</span>
            </div>
            <p className="text-sm text-[#3A4A3E] mb-8">Perfetto per esplorare il fotovoltaico.</p>
            <ul className="space-y-3">
              {FREE_FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  {f.ok ? <Check size={16} className="text-[#166534] mt-0.5 flex-shrink-0" /> : <X size={16} className="text-[#991B1B] mt-0.5 flex-shrink-0" />}
                  <span className={f.ok ? "text-[#17301C]" : "text-[#6E7A71] line-through"}>{f.text}</span>
                </li>
              ))}
            </ul>
            <button disabled className="mt-8 w-full px-6 py-3 rounded-full border-2 border-[#DDE0D8] text-[#6E7A71] font-semibold cursor-not-allowed" data-testid="plan-free-btn">
              Piano attuale
            </button>
          </motion.div>

          {/* PRO */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="relative rounded-2xl p-8 bg-[#17301C] text-white border-2 border-[#EAB308]">
            <div className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-[#EAB308] text-[#17301C] text-xs font-black uppercase tracking-widest flex items-center gap-1">
              <Sun size={11} /> Consigliato
            </div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#EAB308] mb-3">Pro</div>

            <ul className="space-y-3 mb-8">
              {PRO_FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check size={16} className="text-[#EAB308] mt-0.5 flex-shrink-0" />
                  <span className="text-white">{f.text}</span>
                </li>
              ))}
            </ul>

            {user?.is_pro ? (
              <div className="w-full px-6 py-3 rounded-full bg-[#EAB308] text-[#17301C] font-black text-center flex items-center justify-center gap-2" data-testid="already-pro">
                <Sun size={16} /> Sei già Pro
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[10px] font-black tracking-widest uppercase text-white/60 mb-1">Scegli come pagare</div>

                {/* OPTION 1: MONTHLY */}
                <div className="rounded-2xl bg-white/5 border-2 border-white/15 p-5 hover:border-white/40 transition-colors" data-testid="payment-option-monthly">
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="font-black text-white text-lg">Paga ogni mese</div>
                    <div className="text-right">
                      <span className="text-3xl font-black font-mono-nums text-white">€3,50</span>
                      <span className="text-white/70 text-sm">/mese</span>
                    </div>
                  </div>
                  <p className="text-xs text-white/70 mb-4">
                    Addebito automatico su carta ogni mese. Cancelli quando vuoi, nessun impegno.
                  </p>
                  <button
                    onClick={() => startCheckout("monthly")}
                    disabled={!!loadingPlan}
                    data-testid="checkout-monthly-btn"
                    className="w-full px-5 py-3 rounded-full bg-white text-[#17301C] font-black hover:bg-[#EAB308] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loadingPlan === "monthly" ? (
                      <><Loader2 size={16} className="animate-spin" /> Reindirizzamento…</>
                    ) : (
                      <><CreditCard size={16} /> Attiva mensile</>
                    )}
                  </button>
                  <div className="pt-3 mt-3 border-t border-white/10 flex flex-wrap items-center gap-1.5">
                    <PaymentBadge icon={CreditCard} label="Carta" small />
                    <PaymentBadge icon={Smartphone} label="Apple Pay" small />
                    <PaymentBadge icon={Smartphone} label="Google Pay" small />
                  </div>
                </div>

                {/* OPTION 2: ANNUAL */}
                <div className="rounded-2xl bg-[#EAB308]/10 border-2 border-[#EAB308] p-5 relative" data-testid="payment-option-annual">
                  <span className="absolute -top-3 left-5 text-[10px] font-black tracking-widest uppercase px-2 py-1 rounded-full bg-[#EAB308] text-[#17301C]">
                    Risparmi 17%
                  </span>
                  <div className="flex items-baseline justify-between mb-1 mt-1">
                    <div className="font-black text-white text-lg">Paga tutto subito</div>
                    <div className="text-right">
                      <span className="text-3xl font-black font-mono-nums text-white">€35</span>
                      <span className="text-white/70 text-sm">/anno</span>
                    </div>
                  </div>
                  <p className="text-xs text-white/70 mb-3">
                    Un pagamento unico. Equivalente a <b>€2,92/mese</b>. Nessun rinnovo automatico.
                  </p>
                  <div className="mb-4 p-2.5 rounded-lg bg-[#EAB308]/20 border border-[#EAB308]/40">
                    <div className="flex items-center gap-2 text-[#EAB308] font-bold text-[10px] uppercase tracking-widest">
                      <Wallet size={12} /> Anche in 3 rate senza interessi
                    </div>
                    <div className="text-white/85 text-xs mt-0.5">
                      Con Klarna o Scalapay: <b>€11,67 × 3</b>
                    </div>
                  </div>
                  <button
                    onClick={() => startCheckout("annual")}
                    disabled={!!loadingPlan}
                    data-testid="checkout-annual-btn"
                    className="w-full px-5 py-3 rounded-full bg-[#EAB308] text-[#17301C] font-black hover:bg-[#f5bc1b] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loadingPlan === "annual" ? (
                      <><Loader2 size={16} className="animate-spin" /> Reindirizzamento…</>
                    ) : (
                      <><CreditCard size={16} /> Attiva annuale</>
                    )}
                  </button>
                  <div className="pt-3 mt-3 border-t border-white/10 flex flex-wrap items-center gap-1.5">
                    <PaymentBadge icon={CreditCard} label="Carta" small />
                    <PaymentBadge icon={Smartphone} label="Apple Pay" small />
                    <PaymentBadge icon={Smartphone} label="Google Pay" small />
                    <PaymentBadge label="Klarna" pink small />
                    <PaymentBadge label="Scalapay" purple small />
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        <div className="max-w-2xl mx-auto text-center mt-16 space-y-2">
          <div className="text-xs text-[#6E7A71]">
            Pagamento sicuro via Stripe · Certificato PCI DSS · Cancella in qualsiasi momento
          </div>
          <div className="text-[11px] text-[#6E7A71]">
            Klarna e Scalapay disponibili sul piano annuale. Approvazione soggetta a valutazione.
          </div>
        </div>
      </div>
    </div>
  );
}

function PaymentBadge({ icon: Icon, label, pink, purple, blue, small }) {
  const bg = pink
    ? "bg-[#FFA8CD] text-[#0E1B28]"
    : purple
    ? "bg-[#7A2FF7] text-white"
    : blue
    ? "bg-[#003087] text-white"
    : "bg-white/95 text-[#17301C]";
  const size = small ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return (
    <span className={`inline-flex items-center gap-1 rounded-md font-black tracking-wide ${bg} ${size}`}>
      {Icon && <Icon size={small ? 10 : 12} />}
      {label}
    </span>
  );
}
